<template>
    <b-container class="full-height" fluid>
        <b-row class="h-100">
            <b-col cols="3" class="h-100 overflow-auto bg-dark">
                <div class="device-tree-component">
                    <ul class="toolbox">
                        <li class="toolbox-item" @click="toggleSort">
                            <b-icon-sort-alpha-down
                                v-if="isSortAscending()"
                                scale="1.5"
                                v-b-tooltip.hover
                                :title="$t('sorting.desc')"
                            />
                            <b-icon-sort-alpha-up
                                v-else
                                scale="1.5"
                                v-b-tooltip.hover
                                :title="$t('sorting.asc')"
                            />
                        </li>
                        <li class="toolbox-item" @click="toggleDisplay">
                            <b-icon-list-nested
                                v-if="isDisplayTree()"
                                scale="1.5"
                                v-b-tooltip.hover
                                :title="$t('monitoring.display_as_list')"
                            />
                            <b-icon-justify
                                v-else
                                scale="1.5"
                                v-b-tooltip.hover
                                :title="$t('monitoring.display_as_tree')"
                            />
                        </li>
                    </ul>
                    <TreeView
                        :data="treeData.items"
                        :item-children="subItems"
                        :item-is-expanded="expandedIfFewChildren"
                        :item-is-active="itemIsActive"
                        :item-toolbar-actions="itemMenu"
                    >
                        <template scope="_">
                            <div class="icon select-icon-stack" @click="deviceShowOnMapToggle(_.item)">
                                <b-iconstack>
                                    <b-icon stacked icon="square"/>
                                    <b-icon stacked icon="check" v-show="_.item.displayed"/>
                                </b-iconstack>
                            </div>
                            <div class="item-data-container" @click="openDeviceStatus(_.item)">
                                {{prepareText(_.item)}}
                            </div>
                            <ul class="item-actions">
                                <li
                                    v-for="action in itemMenu"
                                    v-show="action.isVisible(_.item)"
                                    v-bind:key="action.id"
                                    :id="`action-${_.item.id}-${action.id}`"
                                >
                                    <b-icon
                                        :icon="action.icon"
                                        class="action"
                                        v-bind:class="{active: action.isActive(_.item)}"
                                        @click="action.itemClicked(_.item)"
                                    />
                                    <b-tooltip
                                        :target="`action-${_.item.id}-${action.id}`"
                                        triggers="hover"
                                        placement="bottom"
                                        :delay="{show: 100, hide: 100}"
                                    >
                                        {{action.text}}
                                    </b-tooltip>
                                </li>
                            </ul>
                        </template>
                    </TreeView>
                </div>
            </b-col>
            <b-col cols="9">
                <DeviceMap
                    :devices-on-map-list="displayedDeviceList()"
                    :followed-devices="followedDeviceList()"
                    :focused-device="focusedDevice"
                    :focus-on-device="deviceOnMapClicked"
                />
            </b-col>
        </b-row>
        <b-sidebar
            right
            bg-variant="dark"
            text-variant="light"
            v-model="showSidebar"
            no-header
            width="420px"
            ref="sidebar"
        >
            <template>
                <div class="sidebar-header">
                    <h4 v-if="deviceCommands">{{$t('menu.commands')}}</h4>
                    <h4 v-if="deviceStatus">{{$t('device.status.title')}}</h4>
                    <b-icon-x class="icon" @click="closeSidebar" scale="1.5" />
                </div>
                <div class="sidebar-content">
                    <template v-if="deviceCommands">
                        <DeviceCommands
                            :device="deviceCommands"
                        />
                    </template>
                    <template v-if="deviceStatus">
                        <DeviceStatus
                            :device="deviceStatus"
                        />
                    </template>
                </div>
            </template>
        </b-sidebar>
    </b-container>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import DeviceMap from "./DeviceMap.vue";
import DeviceStatus from "./DeviceStatus.vue";
import DeviceCommands from "./DeviceCommands.vue";
import TreeView, {ITreeItemAction} from "../Common/TreeView.vue";
import {IDeviceDto, IDeviceGroupDto} from "../../Repository/DeviceGroup/Dto/IDeviceDto";
import DeviceGroupRepository from "../../Repository/DeviceGroup/DeviceGroupRepository";

interface ITreeItemGroup {
    items: ITreeItem[] | null,
}

export interface ITreeItem extends ITreeItemGroup {
    id: number,
    name: string,
    active: boolean,
    displayed: boolean,
    followed: boolean,
}

enum TreeSorting {
    Ascending,
    Descending
}

enum DisplayStyle {
    TREE,
    LIST
}

@Component({
    components: { DeviceMap, DeviceStatus, DeviceCommands, TreeView }
})
class Monitoring extends Vue {
    showSidebar: boolean = false;
    treeData: ITreeItemGroup = {
        items: [],
    };
    sourceData: IDeviceDto[] = [];
    sort: TreeSorting = TreeSorting.Ascending;
    display: DisplayStyle = DisplayStyle.TREE;
    deviceStatus: number[]|null = null;
    deviceCommands: number|null = null;
    focusedDevice: number[] = [];

    get itemMenu(): ITreeItemAction[] {
        return [
            {
                id: 2,
                icon: 'disc',
                text: this.$t('device.focus').toString(),
                isVisible: (item: ITreeItem) => (!item.items || !item.items.length) &&  item.displayed,
                isActive: () => false,
                itemClicked: (item: ITreeItem) => {
                    if (!item.items || !item.items.length) {
                        this.focusedDevice = [ item.id ];
                    }
                }
            },
            {
                id: 0,
                icon: 'fullscreen',
                text: this.$t('device.follow').toString(),
                isVisible: (item: ITreeItem) => item.displayed,
                isActive: (item: ITreeItem) => item.followed,
                itemClicked: (item: ITreeItem) => item.followed = !item.followed,
            },
            {
                id: 1,
                icon: 'chat-left-dots',
                text: this.$t('settings.packets').toString(),
                isVisible: (item: ITreeItem) => !item.items || !item.items.length,
                isActive: () => false,
                itemClicked: (item: ITreeItem) => this.openDeviceMessages(item),
            },
            {
                id: 4,
                icon: 'play-circle',
                text: this.$t('menu.commands').toString(),
                isVisible: (item: ITreeItem) => !item.items?.length,
                isActive: () => false,
                itemClicked: (item: ITreeItem) => this.openCommands(item),
            },
            {
                id: 3,
                icon: 'wrench',
                text: this.$t('menu.device_props').toString(),
                isVisible: (item: ITreeItem) => !item.items?.length,
                isActive: () => false,
                itemClicked: (item: ITreeItem) => this.openDeviceProps(item),
            },
        ];
    }

    mounted() {
        this.updateDeviceData();
    }

    updateDeviceData(): void {
        DeviceGroupRepository
            .getAllDevicesWithGroups(this.$store)
            .then((data: IDeviceDto[]) => {
                this.sourceData = data;
                this.createTreeData();
            });
    }

    toggleSort(): void {
        this.sort = this.sort === TreeSorting.Ascending ? TreeSorting.Descending : TreeSorting.Ascending;
        if (this.sort === TreeSorting.Ascending) {
            this.sortAsc();
        } else {
            this.sortDesc();
        }
    }

    toggleDisplay(): void {
        this.display = this.display === DisplayStyle.TREE ? DisplayStyle.LIST : DisplayStyle.TREE;
        this.createTreeData();
    }

    sortAsc(): void {
        this.treeData.items = this.treeData.items?.sort(
            (e1: ITreeItem, e2: ITreeItem): number => {
                const firstName = this.prepareText(e1);
                const secondName = this.prepareText(e2);
                return firstName === secondName ? 0 : (firstName > secondName ? 1 : -1);
            }
        ) ?? null;
    }

    sortDesc(): void {
        this.treeData.items = this.treeData.items?.sort(
            (e1: ITreeItem, e2: ITreeItem): number => {
                const firstName = this.prepareText(e1);
                const secondName = this.prepareText(e2);
                return firstName === secondName ? 0 : (firstName < secondName ? 1 : -1);
            }
        ) ?? null;
    }

    isSortAscending(): boolean {
        return this.sort === TreeSorting.Ascending;
    }

    isDisplayTree(): boolean {
        return this.display === DisplayStyle.TREE;
    }

    openDeviceStatus(item: ITreeItem): void {
        if (item.items?.length) {
            return;
        }
        this.deviceStatus = [item.id];
        this.deviceCommands = null;
        this.showSidebar = true;
        this.forEachItem(item => item.active = false);
        item.active = true;
    }

    openDeviceMessages(item: ITreeItem): void {
        this.$router.push({ path: `messages/${item.id}`});
    }

    openCommands(item: ITreeItem): void {
        this.deviceCommands = item.id;
        this.deviceStatus = null;
        this.showSidebar = true;
    }

    subItems(item: ITreeItem): ITreeItem[]|null {
        return item.items;
    }

    expandedIfFewChildren(item: ITreeItem): boolean {
        return !!item.items && item.items.length <= 5;
    }

    itemIsActive(item: ITreeItem): boolean {
        return item.active;
    }

    deviceShowOnMapToggle(item: ITreeItem): void {
        item.displayed = !item.displayed;
        if (item.items) {
            item.items.forEach(q => q.displayed = item.displayed);
        }
    }

    prepareText(item: ITreeItem): string {
        return item.name + (item.items && item.items.length ? " (" + item.items.length + ")" : "");
    }

    openDeviceProps(item: ITreeItem) {
        this.$router.push({ path: `device-props/${item.id}`});

        if (item.items && item.items.length) {
            return;
        }
        item.displayed = true;

        const processItem = (it: ITreeItem) => {
            if (it.items && it.items.length) {
                it.items.forEach(i => processItem(i));
            } else {
                it.active = it.id === item.id;
            }
        };

        if (!this.treeData.items) {
            this.treeData.items = [];
        }
        this.treeData.items.forEach(i => processItem(i));
    }

    createTreeData(): void {
        const withoutGroup: ITreeItem = {
            active: false,
            id: -1,
            name: this.$t('device.without_group').toString(),
            items: [],
            displayed: true,
            followed: false,
        };
        const mapGroupToTreeItem = (group: IDeviceGroupDto): ITreeItem => {
            return {
                id: group.id,
                name: group.name,
                active: false,
                items: [],
                displayed: true,
                followed: false,
            };
        };
        const mapDeviceToTreeItem = (device: IDeviceDto): ITreeItem => {
            return {
                id: device.id,
                name: `${device.name}` ?? `[ID: ] ${device.id}`,
                active: false,
                items: null,
                displayed: true,
                followed: false,
            };
        };
        const addDeviceToTreeGroup = (group: ITreeItem, device: IDeviceDto): void => {
            if (!group.items) {
                group.items = [];
            }
            group.items.push(mapDeviceToTreeItem(device));
        };

        const displaySourceAsTree = (items: ITreeItem[]): void => {
            this.sourceData.forEach((device: IDeviceDto) => {
                if (!device.groupList.length) {
                    addDeviceToTreeGroup(withoutGroup, device);
                }
                device.groupList.forEach(
                    (group: IDeviceGroupDto) => {
                        let groupIdx = items.findIndex(q => q.id === group.id);
                        if (groupIdx === -1) {
                            groupIdx = items.push(mapGroupToTreeItem(group)) - 1;
                        }
                        addDeviceToTreeGroup(items[groupIdx], device);
                    }
                )
            });

            if (withoutGroup.items && withoutGroup.items.length) {
                items.push(withoutGroup);
            }

            items.forEach(
                item => {
                    if (item.items && item.items.length > 5) {
                        item.items.forEach(item => item.displayed = false);
                    }
                }
            );
        };

        const displaySourceAsList = (items: ITreeItem[]): void => {
            this.sourceData.map((device: IDeviceDto) => {
                items.push(mapDeviceToTreeItem(device));
            });
        }

        const items: ITreeItem[] = [];

        switch (this.display) {
            case DisplayStyle.TREE: {
                displaySourceAsTree(items);
                break;
            }

            case DisplayStyle.LIST: {
                displaySourceAsList(items);
                break;
            }
        }

        this.treeData.items = items;
    }

    displayedDeviceList(): number[] {
        const list: number[] = [];
        this.forEachItem(item => {
            if (item.displayed) {
                list.push(item.id);
            }
        });
        return list;
    }

    followedDeviceList(): number[] {
        const list: number[] = [];
        this.forEachItem(item => {
            if (item.followed) {
                list.push(item.id);
            }
        });
        return list;
    }

    deviceOnMapClicked(deviceId: number): void {
        const item = this.findItem(item => item.id === deviceId);
        if (item) {
            this.openDeviceStatus(item);
        }
    }

    forEachItem(action: (item: ITreeItem) => void): void {
        const processItem = (it: ITreeItem) => {
            if (it.items && it.items.length) {
                it.items.forEach(i => processItem(i));
            } else {
                action(it);
            }
        };
        if (!this.treeData.items) {
            this.treeData.items = [];
        }
        this.treeData.items.forEach(i => processItem(i));
    }

    findItem(predicate: (item: ITreeItem) => boolean): ITreeItem|null {
        const processItem = (node: ITreeItem): ITreeItem|null => {
            if (predicate(node)) {
                return node;
            }
            if (node.items) {
                for (const child of node.items) {
                    const item = processItem(child);
                    if (item) {
                        return item;
                    }
                }
            }
            return null;
        };
        if (!this.treeData.items) {
            this.treeData.items = [];
        }
        for (const child of this.treeData.items) {
            const item = processItem(child);
            if (item) {
                return item;
            }
        }
        return null;
    }

    closeSidebar() {
        if (this.$refs.sidebar) {
            (this.$refs.sidebar as any).hide();
        }
        this.deviceCommands = null;
        this.deviceStatus = [-1];
    }
}

export default Monitoring;
</script>

<style lang="scss" scoped>
    @import '../../custom1.scss';

    .device-tree-component {
        padding-top: 1em;

        .toolbox {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            list-style: none;
            padding: 0;
            margin: 0;

            .toolbox-item {
                @include no-select;

                cursor: pointer;
                width: 32px;
                height: 32px;
                padding: 0;
                margin: 0;
                display: flex;
                align-items: center;
                justify-content: center;

                &:hover {
                    background-color: $primary;
                }
            }
        }
    }

    .full-height {
        height: calc(100vh - 64px);
    }

    .sidebar-header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 1em;

        .icon {
            cursor: pointer;
        }
    }

    .sidebar-content {
        display: flex;
        flex-direction: column;
        padding: 1em;
    }

</style>